import React, { useState, useEffect } from 'react';
import { 
    List, 
    ListItem, 
    Typography, 
    Box, 
    IconButton, 
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Divider,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ListItemText from '@mui/material/ListItemText';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import { setCurrentOrg, setAllOrgs } from '../../store.js';
import useAlert from '../../alert/useAlert.js';
import { renameOrganization, leaveOrganization, createNewOrganization, getOrgRoles, deleteOrganization,  updateTeamLocations, getTeamLocations  } from '../../services/organizationService.js';
import { templates } from '../../constants/templates.js';
import { supabase } from '../../supabaseClient.js';
import OrganizationBankManagement from '../../components/OrgPayouts/OrganizationBankManagement';
import { HK_DISTRICTS } from '../../constants/districts.js';

// Helper Component for Location Row
const LocationRow = ({ location, index, onUpdate, onRemove, isRemoveDisabled }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        <Grid container spacing={1} sx={{ flexGrow: 1 }}>
            <Grid size={4}>
                <FormControl fullWidth size="small">
                    <InputLabel>District</InputLabel>
                    <Select
                        value={location.district || ''}
                        label="District"
                        onChange={(e) => onUpdate(index, 'district', e.target.value)}
                    >
                        {HK_DISTRICTS.map((district) => (
                            <MenuItem key={district} value={district}>{district}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={8}>
                <TextField 
                    fullWidth 
                    size="small"
                    label="Address"
                    value={location.address} 
                    onChange={(e) => onUpdate(index, 'address', e.target.value)}
                    placeholder="Enter complete address"
                    required
                />
            </Grid>
        </Grid>
        <IconButton 
            onClick={() => onRemove(index)} 
            disabled={isRemoveDisabled} 
            color="error"
            sx={{ mt: 0.5 }}
            size="small"
        >
            <DeleteIcon />
        </IconButton>
    </Stack>
);

const ManageOrganization = ({ initialBankOrgId }) => {
    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const allOrgs = useSelector((state) => state.globalState.allOrgs, shallowEqual);
    const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [newOrgName, setNewOrgName] = useState('');
    const [newOrganizationName, setNewOrganizationName] = useState('');
    const [deletePrompt, setDeletePrompt] = useState('');

    // Location Management States
    const [locationsDialogOpen, setLocationsDialogOpen] = useState(false);
    const [orgLocations, setOrgLocations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
    const [newOrgLocations, setNewOrgLocations] = useState([{ id: crypto.randomUUID(), address: '', district: '' }]);

    const dispatch = useDispatch();
    const { setAlert } = useAlert();

    const handleRename = async () => {
        if (!newOrgName.trim()) {
            setAlert('Please enter a new organization name', 'error');
            return;
        }

        const { error } = await renameOrganization(selectedOrg, newOrgName.trim(), userDetails.id, currentOrg.id);
        if (error) {
            setAlert(error.message, 'error');
        } else {
            const updatedOrgs = allOrgs.map(org => org.name === selectedOrg ? { ...org, name: newOrgName } : org);
            dispatch(setAllOrgs(updatedOrgs));
            // dispatch(setCurrentOrg({ id: currentOrg.id, name: newOrgName }));
            if (currentOrg.name === selectedOrg) {
                dispatch(setCurrentOrg({ ...currentOrg, name: newOrgName }));
            }
            setAlert('Organization renamed successfully', 'success');
            setRenameDialogOpen(false);
            setNewOrgName('');
        }
    };

    const handleOpenLocations = async (org) => {
        setSelectedOrg(org.name);
        setSelectedOrgId(org.id);
        setLocationsDialogOpen(true);
        setLoadingLocations(true);
        
        const { data, error } = await getTeamLocations(org.id);
        if (error) {
            setAlert('Failed to load locations', 'error');
            setOrgLocations([]);
        } else {
            // Ensure compatibility if district is missing in old data
            const formattedData = (data || []).map(loc => ({
                ...loc,
                district: loc.district || ''
            }));
            setOrgLocations(formattedData);
        }
        setLoadingLocations(false);
    };

    const handleSaveLocations = async () => {
        // Filter out empty addresses or districts
        const validLocations = orgLocations.filter(l => l.address.trim() !== '' && l.district !== '');
        
        if (validLocations.length !== orgLocations.length) {
             setAlert('Some locations were removed because they were incomplete. Please ensure both District and Address are filled.', 'warning');
        }

        const { error } = await updateTeamLocations(selectedOrgId, userDetails.id, validLocations);
        
        if (error) {
            setAlert(error.message, 'error');
        } else {
            setAlert('Locations updated successfully', 'success');
            setLocationsDialogOpen(false);
        }
    };

    const addLocationField = () => {
        const newLoc = { id: crypto.randomUUID(), address: '', district: '' };
        setOrgLocations([...orgLocations, newLoc]);
    };

    const updateLocationField = (index, field, value) => {
        const updated = [...orgLocations];
        updated[index] = { ...updated[index], [field]: value };
        setOrgLocations(updated);
    };

    const removeLocationField = (index) => {
        const updated = orgLocations.filter((_, i) => i !== index);
        setOrgLocations(updated);
    };

    const handleLeave = async (orgName) => {
        if (allOrgs.length === 1) {
            setAlert('You cannot leave your last organization.', 'error');
            return;
        }
        const { error } = await leaveOrganization(userDetails.id, orgName);
        if (error) {
            if (error.code === 'LAST_ADMIN') {
                setAlert(error.message, 'error');
                setLeaveDialogOpen(false);
            } else if (error.code === 'LAST_MEMBER_ADMIN') {
                setLeaveDialogOpen(false);
                setDeletePrompt(error.message);
                setDeleteDialogOpen(true);
            } else {
                setAlert(error.message, 'error');
            }
        } else {
            const updatedOrgs = allOrgs.filter(org => org.name !== orgName);
            dispatch(setAllOrgs(updatedOrgs));
            if (currentOrg.name === orgName) {
                dispatch(setCurrentOrg({ 
                    id: updatedOrgs[0].id,
                    name: updatedOrgs[0].name
                }));
            }
            setAlert('Left organization successfully', 'success');
            setLeaveDialogOpen(false);
        }
    };

    const addLocationToNewOrg = () => {
        setNewOrgLocations([...newOrgLocations, { id: crypto.randomUUID(), address: '', district: '' }]);
    };

    const updateLocationInNewOrg = (index, field, value) => {
        const updated = [...newOrgLocations];
        updated[index] = { ...updated[index], [field]: value };
        setNewOrgLocations(updated);
    };

    const removeLocationFromNewOrg = (index) => {
        const updated = newOrgLocations.filter((_, i) => i !== index);
        setNewOrgLocations(updated);
    };

    const handleOpenCreateDialog = () => {
        setNewOrganizationName('');
        setNewOrgLocations([{ id: crypto.randomUUID(), address: '', district: '' }]);
        setCreateOrgDialogOpen(true);
    };

    const handleCreateOrg = async () => {
        if (!newOrganizationName.trim()) {
            setAlert('Please enter an organization name', 'error');
            return;
        }

        // Validate Locations
        const validLocations = newOrgLocations.filter(l => l.address && l.address.trim() !== '' && l.district);
        
        if (validLocations.length === 0) {
            setAlert('You must add at least one valid clinic location (District and Address) to create an organization.', 'error');
            return;
        }

        const { error, teamId } = await createNewOrganization(userDetails.id, newOrganizationName.trim(), validLocations);
        if (error) {
            setAlert(error.message, 'error');
        } else {
            dispatch(setCurrentOrg({ id: teamId, name: newOrganizationName }));
            if(allOrgs) {
                dispatch(setAllOrgs([...allOrgs, { id: teamId, name: newOrganizationName }]));
            } else {
                dispatch(setAllOrgs([{ id: teamId, name: newOrganizationName }]));
            }
            setAlert('Organization created successfully', 'success');
            setNewOrganizationName('');
            setCreateOrgDialogOpen(false);
        }
    };

    const handleDelete = async (orgName) => {
        const { error } = await deleteOrganization(userDetails.id, orgName);
        if (error) {
            setAlert(error.message, 'error');
        } else {
            const updatedOrgs = allOrgs.filter(org => org.name !== orgName);
            dispatch(setAllOrgs(updatedOrgs.length > 0 ? updatedOrgs : null));
            if (currentOrg.name === orgName) {
                if(updatedOrgs.length === 0) {
                    dispatch(setCurrentOrg(null));
                } else {
                    dispatch(setCurrentOrg({ 
                        id: updatedOrgs[0].id,
                        name: updatedOrgs[0].name
                    }));
                }
            }
            setAlert('Organization deleted successfully', 'success');
            setDeleteDialogOpen(false);
        }
    };

    return (
        <div>
            {
                allOrgs && (
                  <>
                    <Typography variant="h5" gutterBottom>
                        Manage Organization
                    </Typography>
                    <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '10px', padding: '16px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}>
                        <List sx={{ p: 0 }}>
                            {allOrgs.map((org, index) => (
                                <React.Fragment key={org.id}>
                                    <ListItem
                                        sx={{
                                            py: 2,
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: '#F1F3F5'
                                            }
                                        }}
                                        secondaryAction={
                                            <Stack direction="row" spacing={1}>
                                                <OrganizationBankManagement organization={org} initialOrgId={initialBankOrgId} />
                                                <Tooltip title="Manage Locations" arrow>
                                                    <IconButton
                                                        data-tour="settings-manage-locations"
                                                        onClick={() => handleOpenLocations(org)}
                                                        size="small"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: '#E8F8F7',
                                                            }
                                                        }}
                                                    >
                                                        <LocationOnIcon sx={{ fontSize: 20, color: '#29C0BB' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Rename" arrow>
                                                    <IconButton
                                                        onClick={() => { setSelectedOrg(org.name); setRenameDialogOpen(true); }}
                                                        size="small"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: '#E8F8F7',
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 20, color: '#29C0BB' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Leave" arrow>
                                                    <IconButton
                                                        onClick={() => { setSelectedOrg(org.name); setLeaveDialogOpen(true); }}
                                                        size="small"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: '#E8F8F7',
                                                            }
                                                        }}
                                                    >
                                                        <ExitToAppIcon sx={{ fontSize: 20, color: '#29C0BB' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* {orgRoles[index] === 'Admin' && ( */}
                                                <Tooltip title="Delete" arrow>
                                                    <IconButton
                                                        onClick={() => { setSelectedOrg(org.name); setDeleteDialogOpen(true); }}
                                                        size="small"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: '#FFF4F2',
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 20, color: '#FF4842' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* )} */}
                                            </Stack>
                                        }
                                    >
                                        <ListItemText 
                                            primary={org.name} 
                                            primaryTypographyProps={{
                                                sx: { 
                                                    fontWeight: 500,
                                                    color: '#2C3E50'
                                                }
                                            }}
                                        />
                                    </ListItem>
                                    {index < allOrgs.length - 1 && (
                                        <Divider sx={{ my: 0 }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                  </>  
                )
            }



            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateDialog}
                    sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        backgroundColor: '#29C0BB',
                        fontSize: '1rem',
                        textTransform: 'none',
                        boxShadow: '0px 4px 12px rgba(41, 192, 187, 0.4)',
                        '&:hover': {
                            backgroundColor: '#1FA8A4'
                        }
                    }}
                >
                    Create New Organization
                </Button>
            </Box>

            <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
                <DialogTitle>Rename Organization</DialogTitle>
                <DialogContent>
                    <TextField
                        required
                        autoFocus
                        margin="dense"
                        label="New name"
                        fullWidth
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: '#29C0BB'}} onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                    <Button sx={{color: '#29C0BB'}} onClick={handleRename}>Rename</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
                <DialogTitle>Leave Organization</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to leave this organization?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: '#29C0BB'}} onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
                    <Button sx={{color: '#29C0BB'}} onClick={() => { handleLeave(selectedOrg); setDeletePrompt(''); }}>Leave</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ color: '#FF4842' }}>⚠️ {deletePrompt ? 'Leave Organization' : 'Delete Organization'} </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography>{deletePrompt ? deletePrompt : 'Are you sure you want to delete this organization?'}</Typography>
                        <Typography sx={{ 
                            backgroundColor: '#FFF4F2', 
                            color: '#FF4842',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #FFE9E7'
                        }}>
                            <strong>Warning:</strong> This action is irreversible. {deletePrompt ? "Leaving the organization will permanently remove:" : 'Deleting this organization will permanently remove:'}
                            <ul style={{ marginTop: '8px', marginBottom: '4px' }}>
                                <li>All patient records and their medical data</li>
                                <li>All team member associations</li>
                                <li>All organization settings and configurations</li>
                            </ul>
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: '#29C0BB'}} onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button 
                        sx={{
                            backgroundColor: '#FF4842',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#FF3326'
                            }
                        }} 
                        variant="contained"
                        onClick={() => { handleDelete(selectedOrg); }}
                    >
                        {deletePrompt ? 'Leave' : 'Delete Permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Organization Dialog */}
            <Dialog 
                open={createOrgDialogOpen} 
                onClose={() => setCreateOrgDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Create New Organization</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Organization Name"
                            value={newOrganizationName}
                            onChange={(e) => setNewOrganizationName(e.target.value)}
                            placeholder="Checking Clinic, Hospital Name, etc."
                        />
                        
                        <Divider />
                        
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Clinic Locations (Required)
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                                Please add the district and address for each location. Both are required.
                            </Typography>

                            <Stack spacing={2}>
                                {newOrgLocations.map((loc, index) => (
                                    <LocationRow
                                        key={loc.id || index}
                                        location={loc}
                                        index={index}
                                        onUpdate={updateLocationInNewOrg}
                                        onRemove={removeLocationFromNewOrg}
                                        isRemoveDisabled={newOrgLocations.length === 1}
                                    />
                                ))}
                                
                                <Button 
                                    startIcon={<AddIcon />} 
                                    onClick={addLocationToNewOrg}
                                    sx={{ color: '#29C0BB', borderColor: '#29C0BB', alignSelf: 'flex-start' }}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Another Location
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: '#666'}} onClick={() => setCreateOrgDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleCreateOrg}
                        variant="contained"
                        sx={{
                            backgroundColor: '#29C0BB',
                            '&:hover': { backgroundColor: '#1da8a3' }
                        }}
                    >
                        Create Organization
                    </Button>
                </DialogActions>
            </Dialog>

             <Dialog 
                open={locationsDialogOpen} 
                onClose={() => setLocationsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Manage Locations - {selectedOrg}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Add or update clinic addresses for this organization.
                    </Typography>
                    
                    {loadingLocations ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {orgLocations.map((loc, index) => (
                                <LocationRow
                                    key={loc.id || index}
                                    location={loc}
                                    index={index}
                                    onUpdate={updateLocationField}
                                    onRemove={removeLocationField}
                                    isRemoveDisabled={false}
                                />
                            ))}
                            
                            {orgLocations.length === 0 && (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                    No locations added yet.
                                </Typography>
                            )}
                            
                            <Button 
                                startIcon={<AddIcon />} 
                                onClick={addLocationField}
                                sx={{ color: '#29C0BB', borderColor: '#29C0BB', alignSelf: 'flex-start' }}
                                variant="outlined"
                                size="small"
                            >
                                Add Location
                            </Button>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: '#666'}} onClick={() => setLocationsDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSaveLocations}
                        variant="contained"
                        sx={{
                            backgroundColor: '#29C0BB',
                            '&:hover': { backgroundColor: '#1da8a3' }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ManageOrganization;
